import moment from 'moment';
import { PlatformChartRelationDto } from '@data/_generated-api/platform-api/models/platform-chart-relation-dto';
import { stripSupportMarkup } from '@/app/support/support-markup';
import { HdBodygraphCenter } from '../hd-bodygraph.models';
import { HD_NOT_SELF_BY_IMPACT_ORDER } from '../hd-center-not-self-metadata';
import { getHdPlanetDisplayConfig } from '../planet-grid/hd-planet-display-config';
import { HD_MIND_MOMENT_CATEGORY } from './hd-moment-fields';
import { HdMindConditioner, HdMindMomentExtra, parseHdMindMomentExtra } from './hd-moment-extra';

/**
 * Pure view-model-builders van het "Mijn mind zegt"-logboek (spec
 * 2026-10-22-mind-logboek §5): mind-notities uit de relaties-feed van een chart
 * (`hasMoment` + categorie `mind`, of een bewaard `mind`-blok), gegroepeerd per
 * open centrum in niet-zelf-impactvolgorde, met tellers en de vaakst
 * genoemde conditioneerders. Geen extra API-calls: alles zit in
 * `ChartRelationDto` (linknotitie + `otherChartMomentExtraJson`).
 */

/** Eerste regel van de dialoog in het overzicht; de volledige notitie leest in het Notities-paneel. */
export const HD_MIND_LOG_EXCERPT_LENGTH = 140;

export type HdMindConditionerKind = 'transit' | 'person' | 'none';

export interface HdMindLogEntry {
  readonly relationId: string;
  readonly chartId: string;
  readonly name: string;
  readonly atUtc: string;
  readonly atMs: number;
  readonly dateText: string;
  readonly timeText: string;
  readonly excerpt: string;
  readonly center: HdBodygraphCenter | null;
  readonly gate: number | null;
  readonly conditioners: readonly HdMindConditioner[];
  /** Soorten die voorkomen (leeg = geen conditioneerder); het soortfilter kijkt hiernaar. */
  readonly kinds: readonly ('transit' | 'person')[];
  /** Stabiele sleutels van de conditioneerders (teller), zie `hdMindConditionerKey`. */
  readonly conditionerKeys: readonly string[];
}

export interface HdMindLogGroup {
  /** null = notities zonder centrum (achteraan). */
  readonly center: HdBodygraphCenter | null;
  readonly entries: readonly HdMindLogEntry[];
}

export interface HdMindConditionerCount {
  readonly key: string;
  readonly kind: 'transit' | 'person';
  /** Transit: planeetglyph (Astronomicon) + planeetkleur; persoon: geen glyph. */
  readonly glyph: string | null;
  readonly color: string | null;
  /** Transit: de poort ("64", de lijn wisselt dagelijks); persoon: de chartnaam. */
  readonly label: string;
  readonly planetKey: string | null;
  readonly count: number;
}

/** Een relatie is een mind-notitie bij categorie `mind` of met een bewaard mind-blok. */
export function isHdMindMomentRelation(relation: PlatformChartRelationDto): boolean {
  if (relation.relationType !== 'hasMoment') return false;
  if (relation.otherChartCategory === HD_MIND_MOMENT_CATEGORY) return true;
  return parseHdMindMomentExtra(relation.otherChartMomentExtraJson) != null;
}

export function buildHdMindLogEntry(relation: PlatformChartRelationDto): HdMindLogEntry | null {
  if (!isHdMindMomentRelation(relation) || !relation.otherChartId) return null;
  const mind: HdMindMomentExtra = parseHdMindMomentExtra(relation.otherChartMomentExtraJson) ?? {};
  const atUtc = relation.otherChartMomentUtc ?? '';
  const local = atUtc ? moment.utc(atUtc).local() : null;
  const conditioners = mind.conditioners ?? [];
  return {
    relationId: relation.id ?? `${relation.otherChartId}`,
    chartId: relation.otherChartId,
    name: relation.otherChartName ?? '',
    atUtc,
    atMs: local?.valueOf() ?? 0,
    dateText: local?.format('D MMM YYYY') ?? '',
    timeText: local?.format('HH:mm') ?? '',
    excerpt: hdMindLogExcerpt(relation.note),
    center: mind.center ?? null,
    gate: mind.gate ?? null,
    conditioners,
    kinds: [...new Set(conditioners.map(conditioner => conditioner.kind))],
    conditionerKeys: conditioners.map(conditioner => hdMindConditionerKey(conditioner) as string),
  };
}

/** Alle mind-notities, chronologisch aflopend. */
export function buildHdMindLogEntries(relations: readonly PlatformChartRelationDto[]): HdMindLogEntry[] {
  return relations
    .map(relation => buildHdMindLogEntry(relation))
    .filter((entry): entry is HdMindLogEntry => entry != null)
    .sort((a, b) => b.atMs - a.atMs);
}

/** Groepen per centrum in niet-zelf-impactvolgorde (enkel centra met notities), "zonder centrum" achteraan. */
export function groupHdMindLogEntries(entries: readonly HdMindLogEntry[]): HdMindLogGroup[] {
  const groups: HdMindLogGroup[] = [];
  for (const center of HD_NOT_SELF_BY_IMPACT_ORDER) {
    const own = entries.filter(entry => entry.center === center);
    if (own.length > 0) groups.push({ center, entries: own });
  }
  const without = entries.filter(entry => entry.center == null);
  if (without.length > 0) groups.push({ center: null, entries: without });
  return groups;
}

/** Aantal notities per centrum (voor de filterchips), zonder-centrum onder `null`. */
export function countHdMindLogByCenter(entries: readonly HdMindLogEntry[]): ReadonlyMap<HdBodygraphCenter | null, number> {
  const counts = new Map<HdBodygraphCenter | null, number>();
  for (const entry of entries) counts.set(entry.center, (counts.get(entry.center) ?? 0) + 1);
  return counts;
}

/**
 * De vaakst genoemde conditioneerders (transit per planeet + poort, persoon per chart),
 * aflopend op telling; elke conditioneerder van een notitie telt mee.
 */
export function topHdMindConditioners(entries: readonly HdMindLogEntry[], limit = 3): HdMindConditionerCount[] {
  const counts = new Map<string, HdMindConditionerCount>();
  for (const entry of entries) {
    for (const conditioner of entry.conditioners) {
      const key = hdMindConditionerKey(conditioner);
      if (key == null) continue;
      const existing = counts.get(key);
      if (existing) {
        counts.set(key, { ...existing, count: existing.count + 1 });
        continue;
    }
    if (conditioner.kind === 'transit') {
      const config = getHdPlanetDisplayConfig(conditioner.planetKey);
      counts.set(key, {
        key, kind: 'transit', glyph: config.astronomiconChar, color: config.stripeColor,
        label: `${conditioner.gate}`,
        planetKey: conditioner.planetKey, count: 1,
      });
    } else {
      counts.set(key, { key, kind: 'person', glyph: null, color: null, label: conditioner.chartName, planetKey: null, count: 1 });
    }
    }
  }
  return [...counts.values()]
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, limit);
}

/** Filter op centrum (undefined = alle; null = zonder centrum) en soort conditioneerder (één van de soorten volstaat). */
export function filterHdMindLogEntries(
  entries: readonly HdMindLogEntry[],
  center: HdBodygraphCenter | null | undefined,
  kind: HdMindConditionerKind | 'all',
): HdMindLogEntry[] {
  return entries.filter(entry =>
    (center === undefined || entry.center === center)
    && (kind === 'all' || (kind === 'none' ? entry.kinds.length === 0 : entry.kinds.includes(kind))));
}

/**
 * Transit = planeet + poort (de lijn wisselt dagelijks en zou elke telling
 * versnipperen); persoon = de chart-id.
 */
export function hdMindConditionerKey(conditioner: HdMindConditioner | null | undefined): string | null {
  if (conditioner == null) return null;
  return conditioner.kind === 'transit' ? `transit:${conditioner.planetKey}:${conditioner.gate}` : `person:${conditioner.chartId}`;
}

/** Eerste niet-lege regel van de notitie, zonder opmaak, ingekort op een woordgrens. */
export function hdMindLogExcerpt(note: string | null | undefined): string {
  const plain = stripSupportMarkup(note).split('\n').map(line => line.trim()).find(line => line.length > 0) ?? '';
  if (plain.length <= HD_MIND_LOG_EXCERPT_LENGTH) return plain;
  const cut = plain.slice(0, HD_MIND_LOG_EXCERPT_LENGTH);
  const space = cut.lastIndexOf(' ');
  return `${space > HD_MIND_LOG_EXCERPT_LENGTH / 2 ? cut.slice(0, space) : cut}…`;
}
